<template>
  <div v-if="metrics.length">
    <button
      :id="`accordion-header-${uid}`"
      :aria-expanded="isOpen"
      :aria-controls="`accordion-panel-${uid}`"
      type="button"
      class="w-full rounded-md border border-slate-300 bg-white px-5 py-2.5 shadow-sm outline-none focus:border-transparent focus:ring-1 focus:ring-zuno-turquoise focus:ring-offset-2"
      :class="{ 'border-zuno-turquoise': isOpen }"
      @click="isOpen = !isOpen"
    >
      <section class="flex items-center justify-between">
        <div class="flex flex-col items-start gap-y-1.5">
          <h2 class="font-medium text-md text-primary">
            {{ topic }}
          </h2>
          <span class="text-xs text-gray-400">{{ metrics.length }} Metrics</span>
        </div>
        <button
          type="button"
          class="rounded-full bg-gray-100 p-1.5 outline-none"
        >
          <ChevronDownSmall
            class="transition-all"
            :class="{ 'rotate-180': isOpen }"
          />
        </button>
      </section>
      <Transition
        enter-class="opacity-0"
        enter-active-class="transition-all duration-300"
        enter-to-class="opacity-100"
        leave-class="opacity-100"
        leave-active-class="transition-all duration-200"
        leave-to-class="opacity-0"
      >
        <section
          v-if="isOpen"
          :id="`accordion-panel-${uid}`"
          :aria-labelledby="`accordion-header-${uid}`"
          class="flex flex-col gap-4 mt-4"
        >
          <div
            v-for="(
              {
                id,
                name,
                metadata,
                display_templates,
                metric_expanded,
                framework_indexes,
                assets,
                assignedUsers,
              },
              mIndex
            ) in topicMetrics"
            :key="id"
            class="flex items-center justify-between px-5 py-4 border rounded-md border-slate-200"
          >
            <div class="flex flex-col items-start gap-1">
              <h3 class="text-sm text-primary">
                {{ name }}
              </h3>
              <span class="text-xs text-gray-400">{{ metadata || "" }}</span>
            </div>

            <div class="flex items-center gap-x-3">
              <!-- Manage Assignments -->
              <div
                class="group relative mx-2 flex flex-nowrap items-center gap-x-1 border-r pr-3 text-xs text-[#68737D]"
              >
                <button
                  v-if="assets.length || assignedUsers.length"
                  class="flex items-center gap-1 text-md disabled:cursor-not-allowed"
                  @click.stop="
                    $emit('assignMetricApproval', topicMetrics[mIndex])
                  "
                >
                  <EditUserIcon custom-style="size-4" />
                  <span>Manage Assignments</span>
                </button>
              </div>
              <!-- Assets -->
              <div
                :id="`assets-metric-${id}`"
                class="group relative flex flex-nowrap items-center gap-x-1 text-xs text-[#101828]"
              >
                <div class="flex items-center gap-1 text-md">
                  <BuildingIcon custom-style="size-4" />
                  <span>{{ assets.length }}</span>
                </div>
                <div
                  v-if="assets.length"
                  class="absolute right-0 top-full z-50 hidden rounded-lg border bg-white text-[#344054] shadow group-hover:block"
                >
                  <div class="max-h-[250px] overflow-y-auto rounded-lg text-left">
                    <div class="p-3 text-white bg-main-primary">
                      Assets
                    </div>
                    <span
                      v-for="asset in assets"
                      :key="asset.id"
                      class="block py-2 pl-3 pr-6 whitespace-nowrap"
                    >{{
                      asset.asset_name }}</span>
                  </div>
                </div>
              </div>
              <!-- Users -->
              <div
                :id="`users-metric-${id}`"
                class="group relative flex flex-nowrap items-center gap-x-1 text-xs text-[#101828]"
              >
                <div class="flex items-center gap-1 text-md">
                  <UserCircleIcon custom-style="size-4" />
                  <span>{{ assignedUsers.length }}</span>
                </div>
                <div
                  v-if="assignedUsers.length"
                  class="absolute right-0 top-full z-50 hidden rounded-lg border bg-white text-[#344054] shadow group-hover:block"
                >
                  <div class="max-h-[250px] overflow-y-auto rounded-lg text-left">
                    <div class="p-3 text-white bg-main-primary">
                      Users
                    </div>
                    <div class="flex flex-col">
                      <div
                        v-for="user in assignedUsers"
                        :key="user.id"
                        class="flex items-center justify-between px-3 py-2 gap-x-6 whitespace-nowrap"
                      >
                        <span class="text-medium no-localize-translate">{{ user.name }}</span>
                        <span class="text-xs">{{ user.role }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="display_templates.length"
                class="relative flex flex-nowrap items-center gap-x-1 text-xs text-[#68737D]"
              >
                <div
                  v-for="(version, labelIndex) in display_templates"
                  :key="`${labelIndex}-template-${version}`"
                >
                  <span class="whitespace-nowrap px-2 py-1 text-[#101828]">
                    # {{ version }}
                  </span>
                </div>
                <div
                  v-if="framework_indexes.length"
                  type="button"
                  class="inline-flex py-0 w-fit"
                >
                  <button
                    class="rounded-md bg-gray-100 px-2 py-1 text-[#218579]"
                    @click.stop="toggleMetricTemplates(mIndex)"
                  >
                    +{{ framework_indexes.length }} More
                  </button>
                  <div
                    v-if="metric_expanded"
                    class="pointer-events-none absolute top-[90%] z-10 mt-1.5 flex -translate-x-1/2 flex-col items-start gap-y-2 rounded border bg-white px-3 py-2 shadow"
                  >
                    <div
                      v-for="(template, labelIndex) in framework_indexes"
                      :key="`${labelIndex}-hidden-template${template}`"
                    >
                      <span class="p-3 rounded select-none w-fit whitespace-nowrap">
                        {{ template }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Transition>
    </button>
  </div>
</template>

<script>
import { assetsInjectionKey, usersInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgTopicMetricsAccordion',

  inject: { assetsInjectionKey, usersInjectionKey },

  props: {
    topic: {
      type: String,
      required: true,
    },
    metrics: {
      type: Array,
      required: true,
    },
    companyMembers: {
      type: Array,
      default() {
        return []
      },
    },
    allAssets: {
      type: Array,
      default() {
        return []
      },
    },
  },

  emits: ['assignMetricApproval'],

  data() {
    return {
      uid: null,
      isOpen: false,
      topicMetrics: this.transformTopicMetrics(this.metrics),
    }
  },

  computed: {
    companyAssets() {
      return this.allAssets.length ? this.allAssets : this.assetsInjectionKey
    },
  },

  watch: {
    metrics(newMetrics) {
      this.topicMetrics = this.transformTopicMetrics(newMetrics)
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  methods: {
    transformTopicMetrics(metrics) {
      if (!metrics.length) {
        return []
      }
      return metrics.map((metric) => {
        if (metric.versions) {
          metric.has_frequency_type = true
          metric.framework_indexes = metric.framework_indices.length
            ? [...metric.framework_indices].filter((_, index) => index > 0)
            : []
          metric.display_templates = metric.framework_indices.length
            ? [...metric.framework_indices].filter((_, index) => index === 0)
            : []
        }
        else {
          metric.framework_indexes = []
          metric.display_templates = []
          metric.has_frequency_type = false
        }
        metric.metric_expanded = false
        const { approvers, data_owners } = metric
        const users = [];
        (this.companyMembers.length
          ? this.companyMembers
          : this.usersInjectionKey
        ).forEach(({ name, id }) => {
          const isApprover = approvers.includes(id)
          const isDataOwner = data_owners.includes(id)

          if (isApprover && isDataOwner) {
            users.push({ name, id, role: 'Data Owner and Approver' })
            return
          }
          if (isApprover) {
            users.push({ name, id, role: 'Approver' })
            return
          }
          if (isDataOwner) {
            users.push({ name, id, role: 'Data Owner' })
          }
        })

        metric.assignedUsers = users
        const allAssets = this.allAssets.length ? this.allAssets : this.assetsInjectionKey

        const filteredAssets = metric.assets.filter(asset =>
          asset !== null && typeof asset === 'object'
            ? allAssets.some(item => item.id === asset.id)
            : allAssets.some(item => item.id === asset),
        )
        metric.assets = filteredAssets.map((asset) => {
          const allAsset = this.allAssets.length ? this.allAssets : this.assetsInjectionKey
          asset = typeof asset === 'object' ? asset : allAsset.find(({ id }) => id === Number(asset))
          return asset
        })
        return metric
      })
    },
    toggleMetricTemplates(index) {
      this.topicMetrics[index].metric_expanded
        = !this.topicMetrics[index].metric_expanded
      getCurrentInstance()?.proxy?.$forceUpdate()
    },
  },
}
</script>
